// Recruiting — Numbers (docs/RECRUITING-PLAN.md item 8). Tables only, no
// charts (design law) — hires by source, applications by source, time in
// each stage, outcomes with reasons, hires vs terminations. Chips for
// 30/90/365 days and the four clinics ("All clinics" too, for OWNER/
// DIRECTOR — a MANAGER's own clinic is enforced server-side regardless of
// what this sends, src/recruiting_numbers.js's own resolveClinicParam).
//
// Every number on this page is exactly what GET /api/v1/recruiting/numbers
// returned for the window and clinic asked for — nothing here re-derives,
// rounds, or re-labels a count (rule 9).
import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import { useResource } from '../../lib/store';
import { clinicToday } from '../../lib/clinicDay';
import { addDaysIso } from '../../lib/civilDate';
import { EmptyState, FaultState } from '../FaultState';
import { visibleClinics } from '../ClinicTabs';
import { LOCATION_NAMES, type LocationCode } from '../../lib/locations';
import {
  fetchRecruitingNumbers, recruitingNumbersKey, formatCivilDate,
  type RecruitingNumbers as RecruitingNumbersData,
} from '../../lib/recruitingApi';

const WINDOWS = [
  { id: '30', label: '30 days', days: 30 },
  { id: '90', label: '90 days', days: 90 },
  { id: '365', label: '365 days', days: 365 },
] as const;

const ALL_CLINICS_ID = 'ALL';

// Calendar arithmetic from the clinic's own (Eastern) day — never the UTC day.
function isoDaysAgo(days: number, now = new Date()): string {
  return addDaysIso(clinicToday(now), -days);
}

function NumbersTables({ data }: { data: RecruitingNumbersData }): JSX.Element {
  return (
    <>
      <div class="candidate-columns">
        <section>
          <h3>Hires by source</h3>
          {data.hiresBySource.length === 0 ? <EmptyState label="No hires in this window." /> : (
            <table>
              <thead><tr><th>Source</th><th>Hires</th></tr></thead>
              <tbody>
                {data.hiresBySource.map((r) => <tr key={r.source}><td>{r.source}</td><td>{r.count}</td></tr>)}
              </tbody>
            </table>
          )}
        </section>
        <section>
          <h3>Applications by source</h3>
          {data.applicationsBySource.length === 0 ? <EmptyState label="No applications in this window." /> : (
            <table>
              <thead><tr><th>Source</th><th>Applications</th></tr></thead>
              <tbody>
                {data.applicationsBySource.map((r) => <tr key={r.source}><td>{r.source}</td><td>{r.count}</td></tr>)}
              </tbody>
            </table>
          )}
        </section>
      </div>

      <section>
        <h3>Time in each stage</h3>
        {data.timeInStage.length === 0 ? <EmptyState label="No stage changes in this window." /> : (
          <table>
            <thead><tr><th>Stage</th><th>Transitions</th><th>Median days</th></tr></thead>
            <tbody>
              {data.timeInStage.map((r) => (
                <tr key={r.stage}><td>{r.stage}</td><td>{r.transitions}</td><td>{r.medianDays ?? '—'}</td></tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section>
        <h3>Outcomes</h3>
        {data.outcomes.length === 0 ? <EmptyState label="No outcomes decided in this window." /> : (
          <table>
            <thead><tr><th>Outcome</th><th>Reason</th><th>Count</th></tr></thead>
            <tbody>
              {data.outcomes.map((r, i) => (
                <tr key={`${r.outcome}-${i}`}><td>{r.outcome}</td><td>{r.reason || '—'}</td><td>{r.count}</td></tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <div class="recruiting-tiles">
        <div class="recruiting-tile"><div class="recruiting-tile-num">{data.hires}</div><div class="recruiting-tile-label">Hires</div></div>
        <div class="recruiting-tile"><div class="recruiting-tile-num">{data.terminations}</div><div class="recruiting-tile-label">Terminations</div></div>
      </div>
      <p class="muted">{formatCivilDate(data.from)} – {formatCivilDate(data.to)}, generated {formatCivilDate(data.generatedAt)}.</p>
    </>
  );
}

export function RecruitingNumbers(): JSX.Element {
  const clinics = visibleClinics();
  const [windowId, setWindowId] = useState<typeof WINDOWS[number]['id']>('90');
  const [clinic, setClinic] = useState<string>(clinics.length > 1 ? ALL_CLINICS_ID : (clinics[0] ?? ALL_CLINICS_ID));

  const days = WINDOWS.find((w) => w.id === windowId)?.days ?? 90;
  const from = isoDaysAgo(days);
  const to = clinicToday();
  const query = { from, to, clinic: clinic === ALL_CLINICS_ID ? null : clinic };
  const key = recruitingNumbersKey(query);
  const load = useResource(key, () => fetchRecruitingNumbers(query));

  return (
    <section class="card" data-testid="recruiting-numbers">
      <div class="section-head">
        <h2>Numbers</h2>
      </div>
      <div class="recruiting-chips" data-testid="recruiting-numbers-windows">
        {WINDOWS.map((w) => (
          <button
            key={w.id} type="button" class="btn recruiting-chip" aria-pressed={windowId === w.id}
            data-testid={`recruiting-numbers-window-${w.id}`} onClick={() => setWindowId(w.id)}
          >
            {w.label}
          </button>
        ))}
      </div>
      {clinics.length > 1 ? (
        <div class="recruiting-chips" data-testid="recruiting-numbers-clinics">
          <button
            type="button" class="btn recruiting-chip" aria-pressed={clinic === ALL_CLINICS_ID}
            data-testid="recruiting-numbers-clinic-all" onClick={() => setClinic(ALL_CLINICS_ID)}
          >
            All clinics
          </button>
          {clinics.map((code) => (
            <button
              key={code} type="button" class="btn recruiting-chip" aria-pressed={clinic === code}
              data-testid={`recruiting-numbers-clinic-${code}`} onClick={() => setClinic(code)}
            >
              {LOCATION_NAMES[code as LocationCode] ?? code}
            </button>
          ))}
        </div>
      ) : null}

      {load.status === 'loading' ? <EmptyState label="Loading…" /> : null}
      {load.status === 'fault' ? <FaultState name="recruiting_numbers_load_failed" detail={load.error} /> : null}
      {load.status === 'ready' ? <NumbersTables data={load.data} /> : null}
    </section>
  );
}
